import { specificityToRating } from '@/Util/utils';
import { getFocus } from '@/Common/constants'
import { getLibxert } from '@/xert-wasm';
import type { Signature } from '@/Common/constants';

const elevation_hysteresis = 3

type avgDataObj = {
    count: number,
    max: number,
    total: number,
    avg: number,
}
export type SummaryData = {
    power: avgDataObj,
    speed: avgDataObj,
    cadence: avgDataObj,
    hr: avgDataObj,
    altGain: number,
    distance: number,
    fat: number,
    carbs: number,
    startMpa: number,
    endMpa: number,
    specificity: number,
    focusDuration: number,
    strain: number,
    xep: number
}

export class AnalyticsController {
    /**
     * @param onUpdateSummary Will be called with the summary data after processing the data. Holds only new/updated values (use spread op to merge with existing values)
     */
    private sig: Signature
    public constructor(
        private onUpdateSummary: (summary: Partial<SummaryData>) => void,
    ) {}
    private updateActivitySummary(data, startIndex, endIndex) {
        if (data.length >= startIndex &&
            data.length >= endIndex &&
            startIndex >= 0 &&
            data[startIndex].hasOwnProperty('unix_time') &&
            data[endIndex].hasOwnProperty('unix_time')) {

            const emptyAvgData = () => {
                return {
                    count: 0,
                    max: 0,
                    total: 0,
                    avg: 0,
                }
            }

            let power = emptyAvgData()
            let speed = emptyAvgData()
            let cadence = emptyAvgData()
            let hr = emptyAvgData()
            
            let alt = {
                prev: null,
                gain: 0,
            }
            let strain = {
                tpwr: 0,
                tpr: 0,
                tsr: 0,
                count: 0,
            }
            const getXEP = () => {
                if (strain.tsr != null && strain.tpr != null) {
                    let asr = strain.tsr / strain.count;
                    let apr = strain.tpr / strain.count;
                    let apwr = strain.tpwr / strain.count;

                    return 2 * asr / apr - apwr;
                }
                return 0;
            }
            const getXSS = (startIndex, endIndex) => {
                const sig = this.sig
                const strain = getDiffByProperty('tws', startIndex, endIndex)

                return strain / sig.ftp * 100 * sig.pp / (3.6 * sig.ftp)
            }

            const updateValue_avgType = (propertyKey: string, countObj: avgDataObj, dataObj: any) => {
                let value = dataObj[propertyKey]
                if (!isNaN(value)) {
                    countObj.count++;
                    countObj.total += value;
                    countObj.max = Math.max(countObj.max, value);
                }
            }
            const calcAvg = (countObj: typeof power) => {
                countObj.avg = countObj.total / countObj.count
            }

            const getDiffByProperty = (propertyKey: string, startIndex, endIndex) => {
                const d1 = data[startIndex].hasOwnProperty(propertyKey) ? data[startIndex][propertyKey] : 0;
                const d2 = data[endIndex].hasOwnProperty(propertyKey) ? data[endIndex][propertyKey] : 0;
                return d2 - d1;
            }
            const getProperty = (propertyKey: string, index) => {
                return data[index].hasOwnProperty(propertyKey) ? data[index][propertyKey] : 0;
            }

            for (let i = startIndex; i <= endIndex; i++) {
                const dataObj = data[i];

                updateValue_avgType('power', power, dataObj)
                updateValue_avgType('spd', speed, dataObj)
                updateValue_avgType('cad', cadence, dataObj)
                updateValue_avgType('hr', hr, dataObj)

                if (!isNaN(dataObj.power) && !isNaN(dataObj.mpa)) {
                    const sig = this.sig
                    const pwr = dataObj.power
                    const mpa = dataObj.mpa
                    const pr = (sig.pp - mpa + sig.ftp) / (sig.pp - pwr + sig.ftp);
                    strain.tpwr += pwr;
                    strain.tpr += pr;
                    strain.tsr += pr * pwr;
                    strain.count++;
                }

                if (!isNaN(dataObj.alt)) {
                    let value = dataObj.alt
                    if (alt.prev === null) {
                        alt.prev = value;
                    } else if (value > (alt.prev + elevation_hysteresis)) {
                        alt.gain += (value - alt.prev);
                        alt.prev = value;
                    } else if (value < (alt.prev - elevation_hysteresis)) {
                        alt.prev = value;
                    }
                }
            }

            calcAvg(power)
            calcAvg(speed)
            calcAvg(cadence)
            calcAvg(hr)

            this.onUpdateSummary({
                power,
                speed,
                cadence,
                hr,
                altGain: alt.gain,
                distance: getDiffByProperty('dist', startIndex, endIndex),
                fat: getDiffByProperty('tgf', startIndex, endIndex),
                carbs: getDiffByProperty('tgc', startIndex, endIndex),
                strain: getXSS(startIndex, endIndex),
                xep: getXEP(),
                startMpa: getProperty('mpa', startIndex),
                endMpa: getProperty('mpa', endIndex),
            })

        } else {
            console.error('updateActivitySummary: data is not valid', data)
        }
    }

    /**
     * Will trigger `onUpdateSummary` with the summary after processing the data (expect 2 triggers per call to this function)
     */
    public async processMpaDataSelection(data: any[], startIndex: number, endIndex: number, sig: Signature) {
        // if (segmentEffort && $('.segment-or-lap-selected').length > 0) {
        //     distance = this._selectedDistance / 1000;
        // } else if(segmentEffort == false && $('.segment-or-lap-selected').length > 0){
        //     distance = this._selectedDistance;
        // }
        const start = data[startIndex];
        const end = data[endIndex];
        this.sig = sig
        // Update non-focus values immediately.
        this.updateActivitySummary(data, startIndex, endIndex);

        // let payload: any = {
        //     start,
        //     end,
        //     this.sig,
        //     group: Xert.Activity._groupSegments,
        // }
        // if (segmentEffort) {
        //     // $('.segment-or-lap-selected').LoadingOverlay('show', { force: true });
        //     payload.segment_id = segment_id;
        //     payload.effort_id = effort_id;
        //     payload.userTimeAdjust = this._userTimeAdjust;
        // } else if (lap) {
        //     // $('.segment-or-lap-selected').LoadingOverlay('show', { force: true });
        //     payload.is_lap = true;
        // }

        // if (this._initNoPowerTab)
        //     onFocusData();
        // else

        // if (libxert == null || segmentEffort || lap) {
        //     this._focusSpecificityPromise = $.post(Xert.Activity._focusSpecificityURL, payload, onFocusData);
        // } else 

        const lws = end.lws - start.lws;
        const hws = end.hws - start.hws;
        const pws = end.pws - start.pws;
        const libxert = await getLibxert()
        const specificity = libxert.get_specificity(this.sig.pp, this.sig.ftp, this.sig.atc, lws, hws, pws);
        const fd = libxert.get_focus_duration(this.sig.pp, this.sig.ftp, this.sig.atc, lws, hws, pws);
        // onFocusData(result); (I'm just gonna paste the function down here)
        // this._focusSpecificityPromise = null;

        // $('.segment-or-lap-selected').LoadingOverlay('hide', { force: true });

        // if (segmentEffort && $('.segment-or-lap-selected').length > 0) {
        //     this.expandSegment(startIndex, endIndex, segmentEffort, result.segment, effort_id, result.shared_activities);
        // } else if(segmentEffort == false && $('.segment-or-lap-selected').length > 0){
        //     this.expandLap(startIndex, endIndex, lap, result.lap);
        // }

        // if (result && result.fd) {
        // let focusDuration = result.fd;
        // let specificityRating = result.sr;
        // let focusAthlete = result.fa;
        // let specificity = result.specificity;
        this.onUpdateSummary({
            specificity,
            focusDuration: fd,
        })

        // I just call update instead of this big heavy complicated function
        // this.updateActivitySummaryFocus(data, startIndex, endIndex,
        //     start.unix_time, end.unix_time,
        //     result.fd, result.fa, result.sr, result.specificity);
        // }
        // }
    }
}